import React, { useEffect, useState } from "react";

import landingEmptyLogo from "images/landing-page/landing-empty-logo.png";
import landingPreviewHeaderBG from "images/landing-page/landing-preview-header-bg.png";

import { classnames, Icon, Loader, Panel, PlainButton } from "@culturehq/components";

import { canViewCompaniesFieldLandingPage } from "app/permissions";
import { errorService, getTags, isCentrica, isDeloitte, isMicrosoft, isPremiseHealth, isProgressive, isTarget, t, Trans } from "app/utils";
import { useDoor, useIsTablet } from "app/hooks";

import LandingPageStories from "components/admin/LandingPageStories";
import Tags from "components/common/Tags";
import UsersSlider from "components/admin/settings/UsersSlider";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import HeaderImage from "components/landingPage/HeaderImage";
import { TargetQuoteIcon } from "components/common/icons";

const LandingPagePreviewCallout = () => {
  const safeJsonParse = (value, defaultValue = null) => {
    if (value === null || value === undefined || value === "null" || value === "undefined") {
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      errorService.error(error);
      return defaultValue;
    }
  };

  const getUrl = (isBlob, localUrl) => {
    if (isBlob) {
      return localUrl;
    }

    return localUrl !== "undefined" ? safeJsonParse(localUrl, "") : "";
  };

  const isTablet = useIsTablet();
  const { open: openOnMobile, onToggle: onToggleOpenOnMobile } = useDoor(false);

  const [tags, setTags] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isLogoBlob] = useState(safeJsonParse(localStorage.getItem("isLogoBlob"), false));
  const [orgName] = useState(safeJsonParse(localStorage.getItem("orgName"), ""));
  const [isHeaderImageBlob] = useState(safeJsonParse(localStorage.getItem("isHeaderImageBlob"), false));
  const [storiesState] = useState(safeJsonParse(localStorage.getItem("stories"), []));
  const [usersState] = useState(safeJsonParse(localStorage.getItem("users"), []));
  const [isEducational] = useState(safeJsonParse(localStorage.getItem("isEducational"), false));
  const localLogoUrl = localStorage.getItem("logoUrl");
  const [logoUrl] = useState(getUrl(isLogoBlob, localLogoUrl));
  const localHeaderImageUrl = localStorage.getItem("headerImageUrl");
  const [headerImageUrl] = useState(getUrl(isHeaderImageBlob, localHeaderImageUrl));
  const [headerText] = useState(safeJsonParse(localStorage.getItem("headerText"), ""));
  const [welcomeNote] = useState(safeJsonParse(localStorage.getItem("welcomeNote"), ""));
  const [primaryColor] = useState(safeJsonParse(localStorage.getItem("primaryColor"), "#79b17d"));
  const [companies] = useState(safeJsonParse(localStorage.getItem("companies"), []));
  const [departments] = useState(safeJsonParse(localStorage.getItem("departments"), []));
  const [locations] = useState(safeJsonParse(localStorage.getItem("locations"), []));
  const [interests] = useState(safeJsonParse(localStorage.getItem("interests"), []));
  const [skills] = useState(safeJsonParse(localStorage.getItem("skills"), []));
  const [values] = useState(safeJsonParse(localStorage.getItem("values"), []));
  const [degrees] = useState(safeJsonParse(localStorage.getItem("degrees"), []));
  const [programs] = useState(safeJsonParse(localStorage.getItem("programs"), []));
  const [classYears] = useState(safeJsonParse(localStorage.getItem("classYears"), []));
  const [clubs] = useState(safeJsonParse(localStorage.getItem("clubs"), []));

  const departmentsLabel = safeJsonParse(localStorage.getItem("departmentsLabel"), "");
  const companiesLabel = safeJsonParse(localStorage.getItem("companiesLabel"), "");

  const landingPage = {
    headerImageUrl,
    headerText,
    primaryColor,
    organization: { id: null }
  };

  // Función para manejar filtros seleccionados
  const handleFilterChange = newFilters => {
    setSelectedFilters(newFilters);
  };


  useEffect(
    () => {
      const tags = getTags(values, interests, skills, classYears, clubs);
      const tagsIndex = {
        locations: {},
        departments: {},
        degrees: {},
        programs: {},
        "class years": {}
      };
      (departments || []).forEach(department => {
        tagsIndex.departments[department.id] = department;
      });
      (locations || []).forEach(location => {
        tagsIndex.locations[location.id] = location;
      });
      (degrees || []).forEach(degree => {
        tagsIndex.degrees[degree.id] = degree;
      });
      (programs || []).forEach(program => {
        tagsIndex.programs[program.id] = program;
      });
      (classYears || []).forEach(classYear => {
        tagsIndex["class years"][classYear.id] = classYear;
      });
      tags.forEach(tag => {
        if (!tagsIndex[tag.type]) {
          tagsIndex[tag.type] = {};
        }
        tagsIndex[tag.type][tag.entityId] = tag;
      });

      setTags(tags);
    },
    [values, interests, skills, departments, locations, programs, degrees, classYears, clubs]
  );

  const getSelectedHeaderImage = () => {
    if (typeof headerImageUrl === "string") {
      return headerImageUrl;
    }

    return URL.createObjectURL(headerImageUrl);
  };

  const getSelectedLogo = () => {
    if (typeof logoUrl === "string") {
      return logoUrl;
    }

    return URL.createObjectURL(logoUrl);
  };

  if (!usersState && !storiesState) {
    return <Loader loading />;
  }

  const PreviewHeaderTextStyle = isMicrosoft(orgName) ? {}
    : { backgroundImage: `linear-gradient(120deg, ${primaryColor} 0%, ${primaryColor}38 100%)` };

  const PreviewWelcomeStyle = isMicrosoft(orgName) ? {}
    : { borderColor: `${primaryColor}` };

  return (
    <div className={classnames("LandingPageCallout", "LandingPageHomeCallout", orgName)}>
      <div className="LandingPageCallout--Preview">
        <div className="Preview">
          {!isTarget(orgName) && !isPremiseHealth(orgName) && !isProgressive(orgName) && (
            <div className="Preview-Header__Image" style={{ backgroundImage: headerImageUrl ? `url(${getSelectedHeaderImage()}` : `url(${landingPreviewHeaderBG})` }}>
              <div className="Preview-Header__Overlay">
                <h1 className="Preview-Header__Text" style={PreviewHeaderTextStyle}>
                  {headerText || t("Enter your header text here...")}
                </h1>
              </div>
            </div>
          )}
          {(isTarget(orgName) || isPremiseHealth(orgName) || isProgressive(orgName)) && (
            <HeaderImage
              landingPage={landingPage}
              orgName={orgName}
            />
          )}
          <div className="Preview-Body row">
            <div className="col-md-3 Preview-LeftColumn">
              <div className="Preview-LeftColumn__Container">
                <img className="Preview-Logo col-xs-4 col-md-12" src={logoUrl ? getSelectedLogo() : landingEmptyLogo} alt={t("Logo image")} />
                <div
                  className={
                    classnames(
                      "Filters__button",
                      "toggle-filters",
                      "col-md-12",
                      "col-xs-8"
                    )
                  }
                >
                  <PlainButton onClick={onToggleOpenOnMobile} className="button toggle-filters">
                    <Icon icon="android-options" />
                    {openOnMobile ? <Trans>Hide Filters</Trans> : <Trans>Filter Content</Trans>}
                  </PlainButton>
                </div>
              </div>
              {isProgressive(orgName) && isTablet && (
                <h1 className="Preview-Header__Text visible-md">
                  {headerText || t("Enter your header text here...")}
                </h1>
              )}
              <div className="row">
                {primaryColor && (
                  <div className="Preview-Tags col-xs-8 col-md-12 hidden-md">
                    <Tags
                      entityFormat
                      tags={tags}
                      title={t("tags")}
                      color={primaryColor}
                      selectedFilters={selectedFilters}
                      orgName={orgName}
                    />
                    {isEducational ? (
                      <>
                        <Tags
                          type="programs"
                          tags={programs}
                          title={t("programs")}
                          icon="ios-star-outline"
                          iconFilled="ios-star-outline"
                          color={primaryColor}
                          queryKey="p"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                        <Tags
                          type="degrees"
                          tags={degrees}
                          title={t("degrees")}
                          icon="mortarboard"
                          iconFilled="mortarboard-filled"
                          color={primaryColor}
                          queryKey="de"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                      </>
                    ) : (
                      <>
                        <Tags
                          type="departments"
                          tags={departments}
                          title={t(departmentsLabel)}
                          icon="ios-people-outline"
                          iconFilled="ios-people"
                          color={primaryColor}
                          queryKey="d"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                        {canViewCompaniesFieldLandingPage(orgName) && (
                          <Tags
                            type="companies"
                            tags={companies}
                            title={t(companiesLabel)}
                            icon="building"
                            iconFilled="building-filled"
                            color={primaryColor}
                            queryKey="co"
                            selectedFilters={selectedFilters}
                            orgName={orgName}
                          />
                        )}
                        {!isDeloitte(orgName) && (
                          <Tags
                            type="locations"
                            tags={locations}
                            title={t("locations")}
                            icon="loc-ol"
                            iconFilled="ios-location"
                            color={primaryColor}
                            queryKey="l"
                            selectedFilters={selectedFilters}
                            orgName={orgName}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
                {primaryColor && (
                  <div className="Preview-Tags Preview-Tags__Panel col-xs-12">
                    <Panel className={openOnMobile ? "open" : "closed"}>
                      <Panel.Body>
                        <Tags
                          entityFormat
                          tags={tags}
                          title={t("tags")}
                          color={primaryColor}
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                        {isEducational ? (
                          <>
                            <Tags
                              type="programs"
                              tags={programs}
                              title={t("programs")}
                              icon="ios-star-outline"
                              iconFilled="ios-star-outline"
                              color={primaryColor}
                              queryKey="p"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                            <Tags
                              type="degrees"
                              tags={degrees}
                              title={t("degrees")}
                              icon="mortarboard"
                              iconFilled="mortarboard-filled"
                              color={primaryColor}
                              queryKey="de"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                          </>
                        ) : (
                          <>
                            <Tags
                              type="departments"
                              tags={departments}
                              title={t(departmentsLabel)}
                              icon="ios-people-outline"
                              iconFilled="ios-people"
                              color={primaryColor}
                              queryKey="d"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                            {canViewCompaniesFieldLandingPage(orgName) && (
                              <Tags
                                type="companies"
                                tags={companies}
                                title={t(companiesLabel)}
                                icon="building"
                                iconFilled="building-filled"
                                color={primaryColor}
                                queryKey="co"
                                selectedFilters={selectedFilters}
                                orgName={orgName}
                              />
                            )}
                            <Tags
                              type="locations"
                              tags={locations}
                              title={t("locations")}
                              icon="loc-ol"
                              iconFilled="ios-location"
                              color={primaryColor}
                              queryKey="l"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                          </>
                        )}
                      </Panel.Body>
                    </Panel>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-9">
              {!isProgressive(orgName) && (
                <h1 className="Preview-Header__Text visible-xs">
                  {headerText || t("Enter your header text here...")}
                </h1>
              )}
              {((Object.keys({}).length === 0 && !isCentrica(orgName))
                || isTarget(orgName)) && (
                <div className="Preview-Browser__Welcome">
                  {welcomeNote && (
                    <div className="Preview-Welcome" style={PreviewWelcomeStyle}>
                      <Icon className="Preview-Welcome__Icon" color={primaryColor} icon="quote" />
                      <LazyEditorOutput className="Preview-Welcome__Note" output={welcomeNote || " "} showTags={false} />
                    </div>
                  )}
                </div>
              )}
              {!usersState && !storiesState
                ? <Loader loading />
                : (
                  <>
                    {usersState && usersState.length > 0 && (
                      <div className="Preview-Browser__People">
                        <h2><Trans>Meet our people</Trans></h2>
                        <UsersSlider users={usersState} preview />
                      </div>
                    )}
                    <div className="Preview-Browser__Stories">
                      <h2><Trans>See our stories</Trans></h2>
                      <LandingPageStories
                        stories={storiesState}
                        orgName={orgName}
                        preview
                        users={usersState}
                      />
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPagePreviewCallout;
